export interface Me {
  id: string;
  username: string;
  role: "admin" | "user";
  tiers: ("license" | "wholesale" | "general")[];
  /** still on the first / reset password `<username>1234` — must set its own first (Ben, 2026-10-02) */
  mustChange?: boolean;
  /** an SQ2.0 login — its password lives at SQ2.0 (Ben 2026-10-15) */
  sq?: boolean;
  sqRole?: "manager" | "rep" | null;
  /** may open 👤 ผู้ใช้ — this web's admin or an SQ2.0 manager */
  usersAdmin?: boolean;
}

/** One SQ2.0 login on 👤 ผู้ใช้ (hub adminUsers): appOn = this web, active = the whole login, pw for SQ2.0 managers. */
export type SqStaff = {
  username: string;
  role: "manager" | "rep";
  spCode: string | null;
  display: string;
  fullName?: string | null;
  active: boolean;
  appOn?: boolean;
  websOff?: string[];
  mustChange?: boolean;
  lastLogin?: number | null;
  pw?: string;
  /** manager · sales · opd · purchase · marketing */
  team?: string;
  /** "" = none */
  email?: string;
};

/** บัญชีของฉัน — the signed-in person's own row only */
export type MyAccount = {
  kind: "staff" | "cust";
  username: string;
  name: string | null;
  role: "admin" | "user";
  tiers: Tier[];
  team: string | null;
  email: string | null;
  company?: string | null;
  lastLoginMs?: number | null;
  websOff?: string[] | null;
};

export type Tier = "license" | "wholesale" | "general";

// Data-freshness payload behind the header status strip. `price` is provenance of the live price
// list; `stock` carries the two bbt-rop cron feeds that produce พร้อมขาย (AP). All timestamps are
// epoch ms; nulls mean "no stamp available", not "zero".
export interface Status {
  now: number;
  cached?: boolean;
  price: {
    master: string | null;
    sourceFile: string | null;
    appliedAt: number | null;
    appliedBy: string | null;
    rowsChanged: number | null;
    items: number;
  };
  stock: {
    locAt: number | null;
    reservationsAt: number | null;
    cadence: { onHand: string; loc: string; reservations: string };
  };
}

export const TIER_LABELS: Record<Tier, string> = {
  license: "License",
  wholesale: "Wholesale",
  general: "ราคาขายทั่วไป",
};

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers as Record<string, string> | undefined) },
    ...init,
  });
  if (!r.ok) {
    const err: any = await r.json().catch(() => ({ error: r.statusText }));
    // code + sq (the SQ2.0 links) ride along so the login form can say why and where to go
    throw Object.assign(new Error(err.error ?? r.statusText), { code: err.code as string | undefined, sq: err.sq as string[] | undefined, local: !!err.local });
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  login: (username: string, password: string) => req<{ ok: true; mustChange?: boolean }>("/api/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  /** เข้าครั้งแรก: name + first password + the new one (SQ2.0 setPassword) → signed in */
  loginFirst: (b: { username: string; password: string; next: string; confirm: string }) =>
    req<{ ok: true }>("/api/login/first", { method: "POST", body: JSON.stringify(b) }),
  logout: () => req<{ ok: true }>("/api/logout", { method: "POST" }),
  me: () => req<{ user: Me | null; code?: string; error?: string; sq?: string[] }>("/api/me"),
  changeMyPassword: (b: { current?: string; next: string; confirm?: string }) => req<{ ok: true }>("/api/me/password", { method: "POST", body: JSON.stringify(b) }),
  products: (params: Record<string, string | number | undefined>) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") sp.set(k, String(v));
    return req<{ rows: any[]; total: number; page: number; pageSize: number; tiers: Tier[] }>(`/api/products?${sp}`);
  },
  filters: (productMain?: string) => req<{ brands: string[]; productMains: string[]; productLines: string[]; scopedTo: string | null }>(`/api/filters${productMain ? `?productMain=${encodeURIComponent(productMain)}` : ""}`),
  product: (partnumber: string) => req<any>(`/api/products/${encodeURIComponent(partnumber)}`),
  productStock: (partnumber: string, live = false) => req<{ partnumber: string; bc365_code: string | null; bc_inv: any | null; at: number; source?: string }>(`/api/products/${encodeURIComponent(partnumber)}/stock${live ? "?live=1" : ""}`),
  status: (refresh = false) => req<Status>(`/api/status${refresh ? "?refresh=1" : ""}`),
  // admin
  listUsers: () =>
    req<{ users: any[]; staff: SqStaff[] | null; me: { sq: string | null; sqRole: "manager" | "rep" | null; canWriteStaff: boolean }; sqOk: boolean }>("/api/admin/users"),
  sqCreate: (username: string, fullName?: string) => req<{ ok: true; username: string; pw: string }>("/api/admin/users/sq", { method: "POST", body: JSON.stringify({ username, fullName }) }),
  sqReset: (u: string) => req<{ ok: true; pw: string }>(`/api/admin/users/sq/${encodeURIComponent(u)}/reset`, { method: "POST" }),
  sqSetWeb: (u: string, on: boolean) => req<{ ok: true }>(`/api/admin/users/sq/${encodeURIComponent(u)}/web`, { method: "POST", body: JSON.stringify({ on }) }),
  sqSetTeam: (u: string, team: string) => req<{ ok: true }>(`/api/admin/users/sq/${encodeURIComponent(u)}/team`, { method: "POST", body: JSON.stringify({ team }) }),
  sqSetEmail: (u: string, email: string) => req<{ ok: true }>(`/api/admin/users/sq/${encodeURIComponent(u)}/email`, { method: "POST", body: JSON.stringify({ email }) }),
  myAccount: () => req<{ account: MyAccount }>("/api/me/account"),
  myEmail: (email: string) => req<{ ok: true }>("/api/me/email", { method: "POST", body: JSON.stringify({ email }) }),
  sqSetActive: (u: string, on: boolean) => req<{ ok: true }>(`/api/admin/users/sq/${encodeURIComponent(u)}/active`, { method: "POST", body: JSON.stringify({ on }) }),
  createUser: (b: { username: string; password?: string; role?: "admin" | "user"; tiers?: Tier[]; display_name?: string; company?: string; phone?: string; email?: string }) => req<{ id: string; sq?: boolean; password?: string | null; mustChange?: boolean }>("/api/admin/users", { method: "POST", body: JSON.stringify(b) }),
  updateUser: (id: string, b: any) => req<{ ok: true }>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  deleteUser: (id: string) => req<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),
  tierDefaults: () => req<{ defaults: { tier: Tier; discount_percent: number | null; updated_at: number }[] }>("/api/admin/tier-defaults"),
  setTierDefault: (tier: Tier, discount_percent: number | null) => req<{ ok: true }>(`/api/admin/tier-defaults/${tier}`, { method: "PUT", body: JSON.stringify({ discount_percent }) }),
  applyTierDefault: (tier: Tier) => req<{ ok: true; changes: number }>(`/api/admin/tier-defaults/${tier}/apply`, { method: "POST" }),
  patchProduct: (partnumber: string, b: any) => req<{ ok: true }>(`/api/admin/products/${encodeURIComponent(partnumber)}`, { method: "PATCH", body: JSON.stringify(b) }),
  deleteProduct: (partnumber: string) => req<{ ok: true }>(`/api/admin/products/${encodeURIComponent(partnumber)}`, { method: "DELETE" }),
  uploadXlsx: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/admin/uploads", { method: "POST", body: fd, credentials: "include" });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? r.statusText);
    return r.json() as Promise<{ uploadId: string; rowCount: number; addedCount: number; removedCount: number; sampleAdded: string[]; sampleRemoved: string[]; warnings: any[] }>;
  },
  applyUpload: (id: string) => req<{ ok: true; upserts: number }>(`/api/admin/uploads/${id}/apply`, { method: "POST" }),
  rejectUpload: (id: string) => req<{ ok: true }>(`/api/admin/uploads/${id}`, { method: "DELETE" }),
  listUploads: () => req<{ uploads: any[] }>("/api/admin/uploads"),
  uploadPricePatch: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/admin/uploads/price-patch", { method: "POST", body: fd, credentials: "include" });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? r.statusText);
    return r.json() as Promise<{ uploadId: string; mode: "price-patch"; rowCount: number; patchCount: number; skippedCount: number; skippedSample: string[]; priceChanges: number; warnings: any[] }>;
  },
  applyPricePatch: (id: string) => req<{ ok: true; updates: number }>(`/api/admin/uploads/${id}/apply-price-patch`, { method: "POST" }),
  audit: (before?: number) => req<{ entries: any[] }>(`/api/admin/audit${before ? `?before=${before}` : ""}`),
  exportAll: () => req<{ rows: any[]; tiers: Tier[]; username: string }>("/api/export/all"),
};

export function thb(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export function isCall(row: any): boolean {
  return row?.sale_price == null;
}

export function pct(d: number | null | undefined): string {
  if (d == null) return "—";
  return `${Math.round(d * 1000) / 10}%`;
}
